import { Equipment, EquipmentType } from '../types/project';

// The Dwelling Profile checklist: common fastened-in-place / dedicated-circuit
// loads with per-unit default nameplate values (kW). Checking an item seeds an
// equipment row with load_role 'existing' and value_source 'default'; any edit
// flips the row to 'nameplate'. Defaults are starting points, disclosed on the
// report — items flagged verifyNameplate vary widely in the field and nudge
// the user toward the data tag. HVAC items ship no default at all: their
// ratings vary too much for a canned number to be defensible.
// Spec: docs/specs/traditional-calcs.md §5.3.

export interface DwellingChecklistItem {
  key: string;
  label: string;
  /** Labels this item shipped under before a rename. A checked item seeds a
   * row whose equipment_name IS the label, so the catalog must still
   * recognise rows saved under the earlier wording. */
  legacyLabels?: string[];
  equipmentType: EquipmentType;
  /** Per-unit default in kW; null = no default, user must enter the nameplate. */
  defaultKw: number | null;
  /** High field variance — UI nudges "check the nameplate". */
  verifyNameplate?: boolean;
  /** Plain-language guidance shown beside the item, for equipment whose
   * rating or circuit depends on which kind of unit the home has. */
  note?: string;
  /** Extra fields stamped onto the seeded row — a dryer that plugs into the
   * laundry receptacle starts flagged as served by that circuit, at 120 V. */
  seed?: Partial<Pick<Equipment, 'voltage' | 'on_laundry_circuit'>>;
  /** Highest quantity the picker offers. Omitted = DEFAULT_MAX_QUANTITY.
   * These are single dwelling units, so three of anything is already
   * generous; the exception is space conditioning, where one-to-one ductless
   * heads mean a single home can carry many. */
  maxQuantity?: number;
  group: 'kitchen' | 'laundry-water' | 'hvac' | 'other';
}

/** Quantity ceiling for an ordinary dwelling appliance. */
export const DEFAULT_MAX_QUANTITY = 3;
/** Space-conditioning ceiling: ductless systems run a head per room. */
export const HVAC_MAX_QUANTITY = 8;

export const DWELLING_CHECKLIST: DwellingChecklistItem[] = [
  { key: 'range', label: 'Range / stove (electric)', equipmentType: 'Range', defaultKw: 12, group: 'kitchen' },
  { key: 'range-gas', label: 'Range / stove (gas) — controls & ignition', equipmentType: 'Other', defaultKw: 0.6, group: 'kitchen' },
  { key: 'wall-oven', label: 'Dedicated oven (electric)', legacyLabels: ['Wall oven'], equipmentType: 'Other', defaultKw: 4.5, group: 'kitchen' },
  { key: 'cooktop', label: 'Cooktop (electric)', legacyLabels: ['Cooktop'], equipmentType: 'Other', defaultKw: 7.2, group: 'kitchen' },
  { key: 'dishwasher', label: 'Dishwasher', equipmentType: 'Other', defaultKw: 1.2, group: 'kitchen' },
  { key: 'disposal', label: 'Garbage disposal', equipmentType: 'Other', defaultKw: 0.8, group: 'kitchen' },
  { key: 'microwave', label: 'Microwave (separate circuit)', equipmentType: 'Other', defaultKw: 1.5, group: 'kitchen' },
  { key: 'dryer', label: 'Clothes dryer (electric)', equipmentType: 'Dryer', defaultKw: 5.5, group: 'laundry-water' },
  { key: 'dryer-gas', label: 'Clothes dryer (gas) — motor & ignition', equipmentType: 'Other', defaultKw: 0.6, group: 'laundry-water' },
  // Heat pump dryers and water heaters ship no default for the same reason
  // HVAC does not: a 120 V unit and a 240 V hybrid unit are rated about ten
  // times apart, so no single number is defensible. 220.83 counts both at
  // nameplate either way (items (3)(c) and (3)(d)); a 120 V dryer on the
  // laundry receptacle is instead covered by the circuit item (2) counts.
  {
    key: 'dryer-heat-pump',
    label: 'Clothes dryer (heat pump)',
    equipmentType: 'Dryer',
    defaultKw: null,
    group: 'laundry-water',
    note: 'Ratings vary tenfold, so enter the nameplate. A 120 V ventless model plugs into the laundry receptacle, so this row starts flagged On the laundry circuit and adds nothing beyond the 1,500 VA already counted for that circuit. For a 240 V hybrid model with a resistive booster on its own circuit, set 240 V and clear the flag.',
    seed: { voltage: 120, on_laundry_circuit: true },
  },
  { key: 'water-heater', label: 'Water heater (tank, electric)', legacyLabels: ['Water heater (tank)'], equipmentType: 'Water Heater', defaultKw: 4.5, group: 'laundry-water' },
  { key: 'water-heater-tankless', label: 'Water heater (tankless, electric)', legacyLabels: ['Water heater (tankless)'], equipmentType: 'Water Heater', defaultKw: 18, verifyNameplate: true, group: 'laundry-water' },
  {
    key: 'water-heater-heat-pump',
    label: 'Water heater (heat pump)',
    equipmentType: 'Water Heater',
    defaultKw: null,
    group: 'laundry-water',
    note: 'Enter the nameplate. A 240 V hybrid unit is rated at its backup element, typically 4.5 kW, the same as a standard tank. A 120 V plug-in unit is around a tenth of that.',
  },
  { key: 'heat-pump', label: 'Heat pump', legacyLabels: ['Heat pump / A-C compressor'], equipmentType: 'Heat Pump', defaultKw: null, group: 'hvac', maxQuantity: HVAC_MAX_QUANTITY },
  { key: 'ac-only', label: 'Central A/C (no heat pump)', equipmentType: 'A/C', defaultKw: null, group: 'hvac', maxQuantity: HVAC_MAX_QUANTITY },
  { key: 'space-heat', label: 'Electric space heating', equipmentType: 'Strip Heat', defaultKw: null, group: 'hvac', maxQuantity: HVAC_MAX_QUANTITY },
  // Named by what draws the current, not by what it burns: the blower and
  // controls are the same load whether the burner runs on gas, oil or propane.
  { key: 'furnace-gas', label: 'Furnace — blower & controls', legacyLabels: ['Gas furnace — blower & controls'], equipmentType: 'Other', defaultKw: 0.8, group: 'hvac' },
  // A boiler has no blower, so it is a smaller load than a furnace: burner
  // motor, ignition and circulators. It is still fastened in place and still
  // counts at nameplate. An electric boiler is not this row — that is
  // Electric space heating, which carries the whole heating element.
  {
    key: 'boiler',
    label: 'Boiler — circulator & controls',
    equipmentType: 'Other',
    defaultKw: 0.5,
    group: 'hvac',
    note: 'The burner motor, ignition and circulator pumps of a fuel-fired boiler — gas, oil or propane. A home with several heating zones runs a circulator per zone, so add those up and enter the total from the nameplates. An electric boiler belongs on the Electric space heating row instead.',
  },
  { key: 'evse-existing', label: 'EV charger', legacyLabels: ['EV charger (existing)'], equipmentType: 'EVSE', defaultKw: 9.6, group: 'other' },
  { key: 'well-pump', label: 'Well pump', equipmentType: 'Other', defaultKw: 1.5, group: 'other' },
  { key: 'sump-pump', label: 'Sump pump', equipmentType: 'Other', defaultKw: 0.8, group: 'other' },
  { key: 'pool-pump', label: 'Pool pump', equipmentType: 'Pool Pump', defaultKw: 1.5, group: 'other' },
  { key: 'hot-tub', label: 'Hot tub / spa', equipmentType: 'Other', defaultKw: 6, verifyNameplate: true, group: 'other' },
  // Deliberately not named "sauna heater": profileQuality treats a row whose
  // name contains "heat" as space conditioning, and a sauna must not satisfy
  // the missing-heating check.
  { key: 'sauna', label: 'Electric sauna', equipmentType: 'Other', defaultKw: 6, verifyNameplate: true, group: 'other' },
];

/** The existing row a checklist item controls, looked up by its current
 * label first and then by any label it carried before a rename. */
export function rowForChecklistItem<T>(
  item: DwellingChecklistItem,
  rowsByName: Map<string, T>
): T | undefined {
  const current = rowsByName.get(item.label);
  if (current !== undefined) return current;
  for (const legacy of item.legacyLabels ?? []) {
    const row = rowsByName.get(legacy);
    if (row !== undefined) return row;
  }
  return undefined;
}

/**
 * The quantities the picker offers for an item, always including whatever the
 * project already holds. A saved project may carry a quantity above the
 * ceiling — typed in when these were free-text fields — and the picker must
 * show that number rather than silently reading as something it is not.
 */
export function quantityOptions(item: DwellingChecklistItem, current: number): number[] {
  const max = item.maxQuantity ?? DEFAULT_MAX_QUANTITY;
  const options: number[] = [];
  for (let n = 0; n <= max; n++) options.push(n);
  if (current > max) options.push(current);
  return options;
}

export const CHECKLIST_GROUP_LABELS: Record<DwellingChecklistItem['group'], string> = {
  kitchen: 'Kitchen',
  'laundry-water': 'Laundry & water heating',
  hvac: 'Heating & cooling',
  other: 'Other fixed loads',
};
